import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import { useTheme } from '@mui/material/styles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Grid from '@mui/material/Grid2'
import { useTranslation } from 'react-i18next'
import { IReference } from 'types/cvsInterfaces'
import { IItemVisibility, ILayout } from 'types/layoutInterfaces'
import { chooseDBTranslation } from 'utils/translations'
import VisibilityCheckbox from '../VisibilityCheckbox'
import HideDetailsSettings from './HideDetailsSettings'
import { findHideSetting, hasHiddenDetails } from '../hideSettings'
import colors from 'constants/colors'

interface IReferencesProps {
  references: IReference[]
  layout: ILayout
  hideSection: (value: boolean, type: string) => void
  hideItem: (value: boolean, id: number, type: string) => void
  expanded: boolean
  setStatus: (value: boolean) => void
  handleHideSettings: (data: Record<string, boolean>, type: string) => void
}

/**
 * Renders the References component.
 *
 * @param {Array} references - An array of references.
 * @param {Object} layout - The layout object.
 * @param {Function} hideSection - The function to hide the section.
 * @param {Function} hideItem - The function to hide an item.
 * @param {boolean} expanded - A boolean indicating whether the accordion is expanded.
 * @param {Function} setStatus - The function to set the status.
 * @param {Function} handleHideSettings - Saves the hide setting data of a section.
 * @return {React.ReactElement} The rendered References component.
 * @notExported
 */
const References: React.FC<IReferencesProps> = ({
  references,
  layout,
  hideSection,
  hideItem,
  expanded,
  setStatus,
  handleHideSettings,
}) => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const hideSetting = findHideSetting(layout, 'references')

  let hidden = false
  if (layout && layout.ItemVisibilities && layout.ItemVisibilities.length > 0) {
    const visibilities = layout.ItemVisibilities.filter(
      (visibility: IItemVisibility) => visibility.type === 'references'
    )
    if (visibilities.length > 0) {
      const visibilityMap = new Map(visibilities.map(item => [item.itemId, item.hidden]))
      hidden = references.every(value => visibilityMap.get(value.id) === true)
    }
  }

  const Item: React.FC<{ reference: IReference }> = ({ reference }) => {
    let hiddenItem: boolean | IItemVisibility = false
    if (layout && layout.ItemVisibilities && layout.ItemVisibilities.length > 0) {
      const visibilities = layout.ItemVisibilities.filter(
        (visibility: IItemVisibility) => visibility.type === 'references'
      )
      if (visibilities.length > 0) {
        hiddenItem =
          visibilities.find((value: IItemVisibility) => value.itemId === reference.id && value.hidden === true) ?? false
      }
    }

    return (
      <Box
        display="flex"
        flexDirection="row"
        flexWrap="nowrap"
        width="100%"
        my={2}
        sx={{
          padding: '2px',
          backgroundColor: theme.palette.background.default,
          '&:hover': {
            backgroundColor: theme.palette.background.paper,
            borderRadius: '5px',
          },
        }}
      >
        <Box width="100%">
          <Box display="flex" flexDirection="row" flexWrap="nowrap" width="100%" mb={1}>
            <VisibilityCheckbox
              checked={!!hiddenItem}
              onChange={(checked: boolean) => hideItem(checked, reference.id, 'references')}
            />
            <Box mr={1} fontWeight="fontWeightBold">
              {t('print.reference.name')}:
            </Box>
            <Box display="inline">{`${reference.firstName} ${reference.lastName}`}</Box>
          </Box>

          <Box display="flex" flexDirection="row" flexWrap="nowrap" width="100%" mb={1}>
            <Box mr={1} fontWeight="fontWeightBold">
              {t('print.reference.company')}:
            </Box>
            <Box display="inline">{reference.company}</Box>
          </Box>
          <Box>{chooseDBTranslation(i18n, reference).letter}</Box>
        </Box>
      </Box>
    )
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={(_e, expanded) => setStatus(expanded)}
      elevation={0}
      sx={{ borderRadius: '5px', border: `1px solid ${colors.borderColor}` }}
      slotProps={{
        transition: { unmountOnExit: true },
      }}
    >
      <Box display="flex" alignItems="center">
        <VisibilityCheckbox checked={hidden} onChange={(checked: boolean) => hideSection(checked, 'references')} />
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            flex: 1,
            fontSize: '20px',
            color: theme => theme.palette.primary.main,
            fontWeight: 'bold',
          }}
        >
          <Grid container alignItems="center">
            <Grid>{t('print.layout.references')}</Grid>
            <Grid sx={{ marginLeft: 1, fontSize: '11pt', color: theme.palette.text.secondary, fontWeight: 'normal' }}>
              {hasHiddenDetails(hideSetting, 'references')
                ? t('print.hideSetting.enabled')
                : t('print.hideSetting.disabled')}
            </Grid>
          </Grid>
        </AccordionSummary>
      </Box>
      <AccordionDetails>
        <HideDetailsSettings type="references" layout={layout} handleHideSettings={handleHideSettings} />
        <Box display="flex" flexDirection="column" flexWrap="wrap" width="100%">
          {references.map((reference: IReference, index: number) => {
            return (
              <Box key={`reference-${index}`}>
                <Item reference={reference} />
                {index + 1 < references.length ? <Divider variant="fullWidth" /> : null}
              </Box>
            )
          })}
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}

export default References
